/**
 * TipTap 에디터 관련 유틸리티
 * 
 * TipTap 에디터의 HTML 변환 및 텍스트 추출 기능을 제공합니다.
 */
export { tiptapToHtml, tiptapToText } from './tiptap';

/**
 * HTML 파싱 및 변환 유틸리티
 * 
 * HTML 문자열을 파싱하고 React 요소로 변환하는 기능을 제공합니다.
 */
export { parseHtml, nodesToReactElements, type HtmlNode } from './htmlParser';

/**
 * 파일 관리 유틸리티
 * 
 * 파일 크기 포맷팅과 아이콘 결정 기능을 제공합니다.
 */
export { formatFileSize, getFileIcon } from './fileUtils';

/**
 * 콘텐츠 경로 변환 유틸리티
 * 
 * 임시 경로를 영구 경로로 변환하는 기능을 제공합니다.
 */
export { convertTempPathsToPermanent, convertPopupImageToPermanent } from './contentParser';

/**
 * URL 및 API 관련 유틸리티
 * 
 * 기본 URL과 API URL을 생성하는 기능을 제공합니다.
 */
export { getBaseUrl, getApiUrl } from './baseUrl';

/**
 * 이미지 URL 변환 유틸리티
 * 
 * 업로드된 이미지 경로를 환경에 따라 안전한 URL로 변환하는 기능을 제공합니다.
 */
export { getImageUrl, getPopupImageUrl, getBatchImageUrls } from './imageUrl';

/**
 * 날짜를 안전하게 ISO 문자열로 변환하는 함수
 * 
 * 다양한 형태의 날짜 입력을 받아 ISO 8601 형식의 문자열로 변환합니다.
 * null, undefined, 잘못된 날짜 형식 등에 대해 안전하게 처리하며,
 * 오류 발생 시 현재 시간을 기본값으로 사용합니다.
 * 
 * @param date - 변환할 날짜 (Date 객체, 문자열, null, undefined)
 * @returns ISO 8601 형식의 날짜 문자열
 * 
 * @example
 * ```typescript
 * const isoString = safeToISOString(new Date());
 * console.log(isoString); // "2024-01-15T10:30:00.000Z"
 * 
 * const fromString = safeToISOString("2024-01-15");
 * console.log(fromString); // "2024-01-15T00:00:00.000Z"
 * ```
 */
export function safeToISOString(date: Date | string | null | undefined): string {
  if (!date) {
    return new Date().toISOString();
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toISOString();
  } catch (_error) {
    // 날짜 파싱 실패 시 현재 시간 반환
    return new Date().toISOString();
  }
}

/**
 * 날짜를 안전하게 Date 객체로 변환하는 함수
 * 
 * 다양한 형태의 날짜 입력을 받아 Date 객체로 변환합니다.
 * null, undefined, 잘못된 날짜 형식 등에 대해 안전하게 처리하며,
 * 오류 발생 시 현재 시간을 기본값으로 사용합니다.
 * 
 * @param date - 변환할 날짜 (Date 객체, 문자열, null, undefined)
 * @returns Date 객체
 * 
 * @example
 * ```typescript
 * const dateObj = safeToDate("2024-01-15");
 * console.log(dateObj instanceof Date); // true
 * 
 * const now = safeToDate(null);
 * console.log(now instanceof Date); // true
 * ```
 */
export function safeToDate(date: Date | string | null | undefined): Date {
  if (!date) {
    return new Date();
  }

  try {
    return date instanceof Date ? date : new Date(date);
  } catch (_error) {
    // 날짜 파싱 실패 시 현재 시간 반환
    return new Date();
  }
}

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷팅하는 함수
 * 
 * 다양한 형태의 날짜 입력을 받아 사용자 친화적인 형식으로 변환합니다.
 * 한국어 사용자를 위해 오류 메시지도 한글로 제공합니다.
 * 
 * @param date - 포맷팅할 날짜 (Date 객체, 문자열, null, undefined)
 * @returns YYYY-MM-DD 형식의 날짜 문자열 또는 오류 메시지
 * 
 * @example
 * ```typescript
 * const formatted = formatDate(new Date());
 * console.log(formatted); // "2024-01-15"
 * 
 * const error = formatDate("invalid-date");
 * console.log(error); // "날짜 오류"
 * ```
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) {
    return '날짜 없음';
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date);

    // 유효하지 않은 날짜인 경우 (NaN 체크)
    if (isNaN(dateObj.getTime())) {
      return '날짜 오류';
    }

    // YYYY-MM-DD 형식으로 포맷팅
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (_error) {
    return '날짜 오류';
  }
}

/**
 * 날짜를 YYYY-MM-DD HH:mm 형식으로 포맷팅하는 함수
 * 
 * 시간 정보를 포함한 상세한 날짜 포맷팅을 제공합니다.
 * 한국어 사용자를 위해 오류 메시지도 한글로 제공합니다.
 * 
 * @param date - 포맷팅할 날짜 (Date 객체, 문자열, null, undefined)
 * @returns YYYY-MM-DD HH:mm 형식의 날짜 문자열 또는 오류 메시지
 * 
 * @example
 * ```typescript
 * const formatted = formatDateTime(new Date());
 * console.log(formatted); // "2024-01-15 14:30"
 * 
 * const error = formatDateTime("invalid-date");
 * console.log(error); // "날짜 오류"
 * ```
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) {
    return '날짜 없음';
  }

  try {
    const dateObj = date instanceof Date ? date : new Date(date);

    // 유효하지 않은 날짜인 경우 (NaN 체크)
    if (isNaN(dateObj.getTime())) {
      return '날짜 오류';
    }

    // YYYY-MM-DD HH:mm 형식으로 포맷팅
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (_error) {
    return '날짜 오류';
  }
}

/**
 * 날짜를 HTML input[type="date"]에서 사용할 수 있는 형식으로 변환하는 함수
 * 
 * HTML 날짜 입력 필드의 value 속성에 사용할 수 있는 YYYY-MM-DD 형식으로 변환합니다.
 * 빈 값이나 오류가 있는 경우 빈 문자열을 반환하여 input 필드가 비어있도록 합니다.
 * 
 * @param date - 변환할 날짜 (Date 객체, 문자열, 또는 null/undefined)
 * @returns YYYY-MM-DD 형식의 문자열 또는 빈 문자열
 * 
 * @example
 * ```typescript
 * const inputValue = formatDateForInput(new Date());
 * console.log(inputValue); // "2024-01-15"
 * 
 * const empty = formatDateForInput(null);
 * console.log(empty); // ""
 * ```
 */
export function formatDateForInput(date: Date | string | null | undefined): string {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // 유효한 날짜인지 확인 (NaN 체크)
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    // HTML input[type="date"] 형식으로 변환
    return dateObj.toISOString().split('T')[0];
  } catch (_error) {
    return '';
  }
}

/**
 * 날짜 문자열이 유효한지 확인하는 함수
 * 
 * 주어진 문자열이 유효한 날짜 형식인지 검증합니다.
 * 빈 문자열이나 잘못된 형식의 경우 false를 반환합니다.
 * 
 * @param dateString - 확인할 날짜 문자열
 * @returns 유효한 날짜인지 여부 (true: 유효, false: 유효하지 않음)
 * 
 * @example
 * ```typescript
 * const isValid = isValidDate("2024-01-15");
 * console.log(isValid); // true
 * 
 * const isInvalid = isValidDate("invalid-date");
 * console.log(isInvalid); // false
 * ```
 */
export function isValidDate(dateString: string): boolean {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch (_error) {
    return false;
  }
} 